import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Text, TextLink, Heading, ButtonLink, Skeleton, Flex } from '@indeed/ifl-components';
import { Check, Error as ErrorIcon } from '@indeed/ifl-icons';
import { MarkupComponents } from '@indeed/frontend-markup-react-components';
import { SuccessOrAgreement1, Alert9 } from '@indeed/indeed-design-system-illustrations-spot';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { JSMABridge } from '@indeed/jsma-js-bridge';
import PageTransition from '../../../components/PageTransition/PageTransition';
import GettextFactory from '../../../util/GettextFactory';
import { useSuccessPageQuery, useSavedJobsByKeyQuery } from './__generated__/SuccessPage.generated';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { NavigateToExternalUrl } from '../components/NavigateToExternalUrl';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { useApplyForMeCopy } from '../../auto-apply-setup/hooks/useApplyForMeCopy';
import ApplyForMePromoModal from './ApplyForMePromoModal';
import { APPLY_FOR_ME_PROMO_PATH } from './ApplyForMePromoPage';
import { useApplyForMePromo } from './useApplyForMePromo';
import { AutoApplyRumContext, logAutoApplyRumError } from '../autoApplyRumErrors';

/** Destination the "Set up" promo action sends the user to (supported in a later ticket). */
const APPLY_FOR_ME_SETUP_PATH = '/auto-apply-setup/review/apply-for-me';

/**
 * MyJobs tab the submitted applications live on. `myJobsUrl` from config is the
 * MyJobs base URL, so the tab path is appended here rather than baked into config.
 */
const MY_JOBS_APPLIED_PATH = '/applied';

/**
 * ## Frontend availability classification
 *
 * | Failure                                | `pageFailure`             | Behavior                         |
 * | -------------------------------------- | ------------------------- | -------------------------------- |
 * | Application/server final-state query   | `true`                    | Show the existing failed state   |
 * | Verified zero-submission result        | `true`                    | Show the existing failed state   |
 * | Connectivity without a server response | `true`                    | Show the existing failed state   |
 * | Mixed submission result                | No additional RUM failure | Already reported by Confirmation |
 * | Saved-job or promo fallback            | No RUM failure            | Preserve the success flow        |
 *
 * Load and verification reports are deduplicated by their stable failure key.
 */
const SuccessPage: React.FC = () => {
    const { myJobsUrl, returnUrl, isJobSearchApp } = useInitialState();
    const myJobsAppliedUrl = `${myJobsUrl}${MY_JOBS_APPLIED_PATH}`;
    const gt = GettextFactory();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const eventLogger = useEventLogger();
    const copy = useApplyForMeCopy();
    const { getGroup } = useProctorGroups();

    const [isPromoOpen, setIsPromoOpen] = useState(false);

    // rnhp_auto_apply_banner_tst value 2 (progressive_automation) enables the
    // redesigned success page (subtitle and footnote copy).
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    const isSubmitForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;

    const draftIds = useMemo(
        () => searchParams.get('draftIds')?.split(',').filter(Boolean) ?? [],
        [searchParams],
    );

    // Network-only to avoid stale data: findReviewBeforeSubmitDrafts and the draft
    // mutation/query used earlier in the flow do not share a normalized cache object,
    // so Apollo cannot invalidate this query automatically after submission.
    const { data, loading, error } = useSuccessPageQuery({
        variables: {
            input: {
                includeSubmittedDraft: true,
            },
        },
        skip: draftIds.length === 0,
        fetchPolicy: 'network-only',
    });

    const drafts = useMemo(() => {
        return data?.findReviewBeforeSubmitDrafts?.drafts ?? [];
    }, [data]);
    const draftsMap = useMemo(() => {
        return new Map(drafts.map((d) => [d.applicationDraftId, d]));
    }, [drafts]);
    const numberOfSubmittedApplications = draftIds.filter((id) => {
        const draft = draftsMap.get(id);
        return draft && draft.state === 'SUBMITTED';
    }).length;
    const isApplyForMeActive =
        data?.autoApplyInfo?.autoApplyStatus === 'ENABLED' &&
        data.autoApplyInfo.mode === 'APPLY_FOR_ME';

    const promo = useApplyForMePromo({
        draftIds,
        draftsMap,
        // Server activation is authoritative because local promo storage does not
        // reflect activation completed on another page, device, or session.
        enabled: isProgressiveAutomation && !isSubmitForMeDisabled && !isApplyForMeActive,
    });
    const unsubmittedDrafts = useMemo(() => {
        return draftIds.filter((id) => {
            const draft = draftsMap.get(id);
            return draft && draft.state !== 'SUBMITTED';
        });
    }, [draftIds, draftsMap]);

    const unsubmittedJobKeys = useMemo(() => {
        return unsubmittedDrafts
            .map((id) => draftsMap.get(id)?.applyableJob.clientJobRef)
            .filter(Boolean) as string[];
    }, [unsubmittedDrafts, draftsMap]);

    const { data: savedJobsData, loading: loadingSavedJobs } = useSavedJobsByKeyQuery({
        variables: { keys: unsubmittedJobKeys },
        skip: unsubmittedJobKeys.length === 0,
        fetchPolicy: 'network-only',
    });

    const savedJobKeySet = useMemo(
        () =>
            new Set(
                (savedJobsData?.savedJobsByKey ?? [])
                    .filter((sj) => sj?.state === 'SAVED')
                    .map((sj) => sj?.job.key)
                    .filter(Boolean),
            ),
        [savedJobsData],
    );
    const reportedVerificationFailureKey = useRef<string | null>(null);

    useEffect(() => {
        if (error) {
            const failureKey = `load:${draftIds.join(',')}:${error.message}`;
            if (reportedVerificationFailureKey.current !== failureKey) {
                reportedVerificationFailureKey.current = failureKey;
                logAutoApplyRumError(error, AutoApplyRumContext.SuccessLoad);
            }
        } else if (!loading && data && draftIds.length > 0 && numberOfSubmittedApplications === 0) {
            const failureKey = `verification:${draftIds.join(',')}`;
            if (reportedVerificationFailureKey.current !== failureKey) {
                reportedVerificationFailureKey.current = failureKey;
                logAutoApplyRumError(
                    new Error('No requested applications were submitted'),
                    AutoApplyRumContext.SuccessVerification,
                    true,
                );
            }
        } else if (!loading) {
            reportedVerificationFailureKey.current = null;
        }

        if (!loading && !error && data) {
            draftIds.forEach((id) => {
                const draft = draftsMap.get(id);
                if (!draft) {
                    eventLogger.logReviewError?.({
                        currentPage: location.pathname,
                        errorType: { string: 'draft-load-error' },
                        errorMessage: {
                            string: 'Draft not found in query response',
                        },
                        draftId: { string: id },
                        graphqlOperation: { string: 'findReviewBeforeSubmitDrafts' },
                    });
                } else if (draft.state !== 'SUBMITTED') {
                    eventLogger.logReviewError?.({
                        currentPage: location.pathname,
                        errorType: { string: 'submission-error' },
                        errorMessage: { string: draft.state },
                        draftId: { string: id },
                        graphqlOperation: { string: 'findReviewBeforeSubmitDrafts' },
                    });
                }
            });
        }
    }, [
        loading,
        error,
        data,
        draftIds,
        draftsMap,
        eventLogger,
        location.pathname,
        numberOfSubmittedApplications,
    ]);

    if (draftIds.length === 0) {
        return <NavigateToExternalUrl url={returnUrl} />;
    }

    const pageKey = 'auto-apply-success';
    const helmet = (
        <Helmet>
            <title>{copy.autoApplySuccessTitle}</title>
            <meta
                name="description"
                content={gt.gettext('Your applications have been successfully submitted')}
            />
        </Helmet>
    );

    if (loading) {
        return (
            <PageTransition pageKey={pageKey}>
                {helmet}
                <Flex
                    sx={{
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'border.weak',
                        paddingInline: 5,
                        paddingBlock: 4,
                        borderRadius: 'md',
                        gap: 4,
                    }}
                >
                    {draftIds.map((draftId) => (
                        <Flex
                            key={draftId}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ flex: 1, paddingBlock: '1px' }}>
                                <Skeleton
                                    sx={{ inlineSize: '80%', blockSize: '1rem', marginBlockEnd: 2 }}
                                />
                                <Skeleton sx={{ inlineSize: '60%', blockSize: '1rem' }} />
                            </Box>
                            <Box sx={{ inlineSize: '3.5rem' }}>
                                <Skeleton sx={{ inlineSize: '2rem', blockSize: '2rem' }} />
                            </Box>
                        </Flex>
                    ))}
                </Flex>
            </PageTransition>
        );
    }

    const handlePromoSetUp = () => {
        // Intentionally not suppressing the promo here: tapping "Set up" only sends
        // the user into the Apply For Me setup flow, it does not activate it. If we
        // permanently suppressed on intent, a user who abandons setup would never
        // see the promo again. Server-side activation state suppresses the promo once
        // setup is complete, including activation on another device or session.
        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: 'afm-promo-set-up',
        });
        void navigate(`${APPLY_FOR_ME_SETUP_PATH}${location.search}`);
    };

    const handlePromoDismiss = (button: string) => {
        promo.suppressForDismissal();
        eventLogger.logReviewButtonClick?.({ currentPage: location.pathname, button });
        setIsPromoOpen(false);
        window.location.href = returnUrl;
    };

    // Show the in-page web modal. The web modal does not navigate, so the promo
    // impression is logged here. (The native path deliberately does NOT log here:
    // its webview loads APPLY_FOR_ME_PROMO_PATH, which renders through
    // AutoApplyRoot and logs the impression for that path itself — logging in both
    // places would double-count.)
    const openWebPromo = () => {
        eventLogger.logReviewPageView?.({ currentPage: APPLY_FOR_ME_PROMO_PATH });
        setIsPromoOpen(true);
    };

    // On native, present the promo inside a JSMABridge bottom-sheet webview that
    // reports the user's choice back here, so suppression + navigation run in this
    // (parent) context rather than the modal webview.
    const openNativePromo = async () => {
        try {
            const afmPromoUrl = new URL(APPLY_FOR_ME_PROMO_PATH, window.location.href);
            new URL(window.location.href).searchParams.forEach((value, key) =>
                afmPromoUrl.searchParams.append(key, value),
            );
            const result = await JSMABridge.showModalWithWebViewAndWaitForResult({
                // Absolute path: the native webview doesn't resolve it against the current origin
                url: afmPromoUrl.href,
                presentationMode: 'BOTTOM',
            });
            if (result === 'set-up') {
                handlePromoSetUp();
            } else if (result === 'continue') {
                handlePromoDismiss('afm-promo-continue-manually');
            } else {
                // 'close' button or undefined (native swipe / back gesture) — treat as
                // a dismissal, matching the web "X" behaviour.
                handlePromoDismiss('afm-promo-close');
            }
        } catch (error) {
            // The bridge call rejected (e.g. an older app build without the command).
            // Fall back to the in-page web modal so the promo still shows rather than
            // leaving the user stuck on the (navigation-prevented) success page. No
            // webview loaded, so the web-modal path logs the impression itself.
            console.warn('Apply For Me promo native bridge failed', error);
            openWebPromo();
        }
    };

    const handleBackToHomepageClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: 'back-to-homepage',
        });
        if (!promo.shouldShowPromo()) {
            // Not eligible (incl. error / nothing-submitted screens): let the
            // ButtonLink navigate to returnUrl.
            return;
        }
        event.preventDefault();
        if (isJobSearchApp && JSMABridge.isAvailable()) {
            // In the Job Search mobile app, present the promo through JSMABridge.
            // The impression is logged by AutoApplyRoot when the webview renders
            // APPLY_FOR_ME_PROMO_PATH, so we don't log it here.
            void openNativePromo();
        } else {
            // Web, or native without a usable bridge: show the in-page modal (which
            // logs its own impression).
            openWebPromo();
        }
    };

    const backToHomePageButton = (
        <ButtonLink
            variant="secondary"
            size="md"
            full
            href={returnUrl}
            onClick={handleBackToHomepageClick}
        >
            {gt.gettext('Back to homepage')}
        </ButtonLink>
    );

    if (error) {
        return (
            <PageTransition pageKey={pageKey}>
                {helmet}
                <Flex
                    sx={{
                        flexDirection: 'column',
                        minBlockSize: '100vh',
                        paddingInline: 4,
                        paddingBlockEnd: 6,
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Error content centered */}
                    <Flex
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                            textAlign: 'center',
                            gap: 4,
                        }}
                    >
                        <Alert9 size="md" title={gt.gettext('Error')} aria-hidden />
                        <Text level={3} sx={{ fontWeight: 'bold' }}>
                            {gt.gettext(
                                "We can't load your applications. You can try again later.",
                            )}
                        </Text>
                    </Flex>
                    {backToHomePageButton}
                </Flex>
            </PageTransition>
        );
    }
    const emailAddress = data?.currentUser?.account?.accountProfile?.email?.emailAddress || '';

    if (numberOfSubmittedApplications === 0) {
        return (
            <PageTransition pageKey={pageKey}>
                {helmet}
                <Flex
                    sx={{
                        flexDirection: 'column',
                        minBlockSize: '100vh',
                        paddingInline: 4,
                        paddingBlockEnd: 6,
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Error content centered */}
                    <Flex
                        sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: 1,
                            textAlign: 'center',
                            gap: 4,
                        }}
                    >
                        <Alert9 size="md" title={gt.gettext('Error')} aria-hidden />
                        <Text level={3} sx={{ fontWeight: 'bold' }}>
                            {gt.gettext(
                                "We're having some trouble submitting your applications. You can try again later.",
                            )}
                        </Text>
                    </Flex>
                    {backToHomePageButton}
                </Flex>
            </PageTransition>
        );
    }
    const numberOfDraftIdsNotInDraftsMap = draftIds.filter((id) => !draftsMap.has(id)).length;

    // Single localized string per variant (with/without email) so translators
    // control the sentence spacing and punctuation between both sentences.
    const footnoteText = emailAddress
        ? gt.gettext(
              'You will get a confirmation email at {0}. To view your submitted applications, visit [{1}|myJobsLink].',
              [emailAddress, 'My Jobs'],
          )
        : gt.gettext(
              'You will get a confirmation email. To view your submitted applications, visit [{0}|myJobsLink].',
              ['My Jobs'],
          );

    return (
        <PageTransition pageKey={pageKey}>
            {helmet}
            <Flex
                sx={{
                    flexDirection: 'column',
                    paddingInline: 5,
                }}
            >
                <Heading
                    level={4}
                    sx={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        paddingBlockStart: 4,
                        paddingBlockEnd: isProgressiveAutomation ? 2 : 4,
                        marginBlock: 0,
                    }}
                >
                    {gt.ngettext(
                        '{0} application submitted',
                        '{0} applications submitted',
                        numberOfSubmittedApplications,
                        [numberOfSubmittedApplications],
                    )}
                </Heading>

                {isProgressiveAutomation && (
                    <Text
                        level={2}
                        variant="secondary"
                        sx={{ textAlign: 'center', paddingBlockEnd: 4 }}
                    >
                        {gt.gettext('Find more applications to review on your homepage tomorrow.')}
                    </Text>
                )}

                <Flex sx={{ justifyContent: 'center' }}>
                    <SuccessOrAgreement1
                        size="md"
                        title={gt.gettext('Applications submitted')}
                        aria-hidden
                        sx={{
                            display: 'flex',
                        }}
                    />
                </Flex>

                <Flex
                    sx={{
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'border.weak',
                        paddingInline: 5,
                        paddingBlock: 4,
                        borderRadius: 'md',
                        gap: 4,
                    }}
                >
                    {draftIds.map((draftId) => {
                        const draft = draftsMap.get(draftId);

                        if (!draft) {
                            return null;
                        }

                        const isSubmitted = draft.state === 'SUBMITTED';
                        const companyLocation = [draft.employerName, draft.jobLocation]
                            .filter(Boolean)
                            .join(' · ');

                        return (
                            <Box key={draftId}>
                                <Flex
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box sx={{ flex: 1, paddingInlineEnd: 2 }}>
                                        <Text
                                            variant="contrast"
                                            level={2}
                                            sx={{
                                                fontWeight: 'bold',
                                                display: 'block',
                                            }}
                                        >
                                            {draft.jobTitle}
                                        </Text>
                                        <Text level={2}>{companyLocation}</Text>
                                    </Box>
                                    {isSubmitted ? (
                                        <Check
                                            size="md"
                                            aria-label={gt.gettext('Submitted')}
                                            color="foreground.status.success"
                                        />
                                    ) : (
                                        <ErrorIcon
                                            variant="stroke"
                                            size="md"
                                            aria-label={gt.gettext('Submission issue')}
                                            sx={{ color: 'foreground.status.critical' }}
                                        />
                                    )}
                                </Flex>
                                {!isSubmitted && !loadingSavedJobs && (
                                    <Text
                                        level={1}
                                        sx={{
                                            display: 'block',
                                        }}
                                        variant="alert"
                                    >
                                        {draft.applyableJob.clientJobRef &&
                                        savedJobKeySet.has(draft.applyableJob.clientJobRef)
                                            ? gt.gettext(
                                                  "We couldn't submit your application. Try it later in My Jobs.",
                                              )
                                            : gt.gettext(
                                                  "We're having some trouble submitting this application.",
                                              )}
                                    </Text>
                                )}
                            </Box>
                        );
                    })}

                    {numberOfDraftIdsNotInDraftsMap > 0 && (
                        <Text variant="alert" level={1}>
                            {gt.ngettext(
                                "We're having some trouble submitting {0} other application.",
                                "We're having some trouble submitting {0} other applications.",
                                numberOfDraftIdsNotInDraftsMap,
                                [numberOfDraftIdsNotInDraftsMap],
                            )}
                        </Text>
                    )}
                </Flex>

                {isProgressiveAutomation ? (
                    <Box sx={{ paddingBlock: 4 }}>
                        <Text level={1} variant="secondary">
                            <MarkupComponents
                                components={{
                                    myJobsLink: (children) => (
                                        <TextLink
                                            inline
                                            href={myJobsAppliedUrl}
                                            onClick={() => {
                                                eventLogger.logReviewButtonClick?.({
                                                    currentPage: location.pathname,
                                                    button: 'view-my-jobs',
                                                });
                                            }}
                                        >
                                            {children}
                                        </TextLink>
                                    ),
                                }}
                            >
                                {footnoteText}
                            </MarkupComponents>
                        </Text>
                    </Box>
                ) : (
                    <Box
                        as="ul"
                        sx={{
                            paddingBlock: 4,
                            paddingInlineStart: 4,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {emailAddress ? (
                            <li>
                                <Text level={2} variant="secondary">
                                    {gt.gettext('You will get a confirmation email at {0}.', [
                                        emailAddress,
                                    ])}
                                </Text>
                            </li>
                        ) : (
                            <li>
                                <Text level={2} variant="secondary">
                                    {gt.gettext('You will get a confirmation email.')}
                                </Text>
                            </li>
                        )}
                        <li>
                            <Text level={2} variant="secondary">
                                {gt.gettext(
                                    'Review more applications tomorrow from your homepage.',
                                )}
                            </Text>
                        </li>
                        <li>
                            <Text level={2} variant="secondary">
                                <MarkupComponents
                                    components={{
                                        myJobsLink: (children) => (
                                            <TextLink
                                                href={myJobsAppliedUrl}
                                                onClick={() => {
                                                    eventLogger.logReviewButtonClick?.({
                                                        currentPage: location.pathname,
                                                        button: 'view-my-jobs',
                                                    });
                                                }}
                                            >
                                                {children}
                                            </TextLink>
                                        ),
                                    }}
                                >
                                    {gt.gettext(
                                        'To view your submitted applications, visit [{0}|myJobsLink].',
                                        ['My Jobs'],
                                    )}
                                </MarkupComponents>
                            </Text>
                        </li>
                    </Box>
                )}
                {backToHomePageButton}
            </Flex>
            <ApplyForMePromoModal
                isOpen={isPromoOpen}
                onSetUp={handlePromoSetUp}
                onContinue={() => handlePromoDismiss('afm-promo-continue-manually')}
                onClose={() => handlePromoDismiss('afm-promo-close')}
            />
        </PageTransition>
    );
};

export default SuccessPage;
